import React from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';

import DriverSelect from '@features/drivers/components/DriverSelect';
import { useGetPartsDriversQuery } from '../queries/getPartsDrivers.query.generated';
import { useRooftop, useUser } from '@store';

interface ReassignDriverDialogProps {
  open: boolean;
  onClose: () => void;
  runId: number;
  /** Driver currently on the run — filtered out of the picker. */
  currentDriverId: number | null;
  /** Shown in the body as context ("Reassigning from {name}…"). */
  currentDriverName?: string | null;
  /** Called after a successful reassign/dispatch so the page can refetch. */
  onDone?: () => void;
}

/**
 * Reassign a parts-delivery run to a different driver (or first-dispatch a
 * draft/unassigned run). Mirrors the mobile-service ReassignDriverDialog —
 * POSTs to the reassignPartsDeliveryRun Netlify function with a newDriverId.
 */
const ReassignDriverDialog: React.FC<ReassignDriverDialogProps> = ({
  open,
  onClose,
  runId,
  currentDriverId,
  currentDriverName,
  onDone,
}) => {
  const user = useUser();
  const rooftop = useRooftop();
  const customerId = rooftop?.id || user?.profile?.customer_id || 0;

  // One dialog, two modes: straight reassign (driver exists) and first-dispatch
  // from a run with no driver yet. Copy adapts; server mechanic is identical.
  const isFirstDispatch = currentDriverId == null;
  const titleText = isFirstDispatch ? 'Dispatch run' : 'Reassign driver';
  const actionLabel = isFirstDispatch ? 'Dispatch' : 'Reassign';
  const inFlightLabel = isFirstDispatch ? 'Dispatching…' : 'Reassigning…';
  const successVerb = isFirstDispatch ? 'dispatched' : 'reassigned';

  const [selectedDriverId, setSelectedDriverId] = React.useState<number | null>(null);
  const [submitting, setSubmitting] = React.useState(false);

  const { data: driversData, loading: driversLoading } = useGetPartsDriversQuery({
    variables: { customerId },
    skip: !customerId || !open,
  });

  const drivers = React.useMemo(() => {
    const all = driversData?.drivers ?? [];
    return currentDriverId != null ? all.filter(d => d.id !== currentDriverId) : all;
  }, [driversData, currentDriverId]);

  React.useEffect(() => {
    if (open) setSelectedDriverId(null);
  }, [open]);

  const handleConfirm = async () => {
    if (!selectedDriverId) {
      toast.warning(`Select a driver to ${actionLabel.toLowerCase()} to`);
      return;
    }
    setSubmitting(true);
    try {
      const token = await user?.getToken();
      if (!token) {
        throw new Error('Authentication failed — could not get token');
      }
      const baseUrl = window.location.origin;
      const res = await axios({
        method: 'POST',
        url: `${baseUrl}/.netlify/functions/reassignPartsDeliveryRun`,
        data: { runId, newDriverId: selectedDriverId },
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!res.data?.success) {
        throw new Error(res.data?.error || `${actionLabel} returned non-success`);
      }
      const newDriver = drivers.find(d => d.id === selectedDriverId);
      toast.success(
        `Run ${successVerb} to ${newDriver?.user?.display_name || `driver #${selectedDriverId}`}`,
      );
      onDone?.();
      onClose();
    } catch (err: any) {
      // Server returns 409 with a friendly message when the run already
      // started — surface it verbatim.
      const serverMsg = err?.response?.data?.error;
      toast.error(serverMsg || err?.message || `${actionLabel} failed`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth='sm' fullWidth>
      <DialogTitle>{titleText}</DialogTitle>
      <DialogContent dividers>
        {currentDriverName ? (
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            Reassigning from <strong>{currentDriverName}</strong>. The current driver
            will be notified the run was taken off their list, and the new driver will
            receive a fresh offer.
          </Typography>
        ) : (
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            Pick a driver to dispatch this run to. They'll receive a targeted offer and
            the run will move to <strong>dispatched</strong> until they accept.
          </Typography>
        )}
        <Box sx={{ minHeight: 320 }}>
          <DriverSelect
            drivers={drivers}
            selectedDriverId={selectedDriverId}
            onSelect={id => setSelectedDriverId(id)}
            loading={driversLoading}
            label='Select a driver'
            filterPlaceholder='Search drivers...'
            emptyMessage='No available drivers'
            maxHeight={360}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant='contained'
          onClick={handleConfirm}
          disabled={submitting || !selectedDriverId}
          startIcon={submitting ? <CircularProgress size={16} color='inherit' /> : undefined}>
          {submitting ? inFlightLabel : actionLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReassignDriverDialog;
